import { orderFormSchema } from '@common/order/orderSchema';
import { orderStore, setOrderStore } from '@store/orders/store';
import { ICustomerPayment, OrderState, TabState } from '@store/orders/types';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { LTLLoadItem } from '@store/ltl/types';
import { setLoadItems } from '@store/ltl/store';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { logError } from '@utils/errorLogging';
import { validateOrder } from '@utils/validationUtils';
import cloneDeep from 'lodash/cloneDeep';
import { produce, unwrap } from 'solid-js/store';
import { ValidationError } from 'yup';
import { synchronizeOrderChangeHydration } from '@changeTracking/pages/orderDetails/formChangeTracking';

export const updateOrderField = (field: string, value: unknown) => {
  if (field === 'loadsPayments') {
    setOrderStore(
      produce((draft) => {
        if (
          draft.order.loads.length > 0 &&
          draft.order.loads[orderStore.activeTab.index].payments
        ) {
          draft.order.loads[orderStore.activeTab.index].payments?.push(
            value as ICustomerPayment,
          );
        }
      }),
    );
  }
};

export const updateOrderState = (updates: Partial<IOrderViewModel>) => {
  Object.entries(updates).forEach(([key, value]) => {
    setOrderStore('order', key as keyof IOrderViewModel, value);
  });
  if (orderStore.order.operationType === 'None') {
    setOrderStore('order', 'operationType', 'Update');
  }
};

/** Wrap a synchronous store update after its API request has completed. */
export const applyOrderHydration = (apply: () => void) => {
  const before = cloneDeep(unwrap(orderStore.order));
  apply();
  synchronizeOrderChangeHydration(before, unwrap(orderStore.order));
};

export const updateLoadPropertyAtIndex = (
  propertiesToUpdate: Partial<ILoadEditViewModel>,
  idx?: number, // this change is added so as to update the load at a specific index of choice or by default it will set to the active tab index
) => {
  setOrderStore(
    'order',
    'loads',
    produce((loads) => {
      for (const property in propertiesToUpdate) {
        if (propertiesToUpdate.hasOwnProperty(property)) {
          const value =
            propertiesToUpdate[property as keyof ILoadEditViewModel];
          const index = idx ?? orderStore.activeTab.index;
          if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean' ||
            value === null ||
            value === undefined
          ) {
            if (
              !('operationType' in loads[index]) ||
              loads[index].operationType === 'None'
            ) {
              loads[index].operationType = 'Update';
            }
          }
          (loads[index] as unknown as Record<string, unknown>)[property] =
            value;
          if (property === 'items') {
            const t = value as LTLLoadItem[];
            setLoadItems(
              t.map((item) => {
                return {
                  ...item,
                  item: item.item,
                  description: item.description,
                  type: item.item,
                };
              }),
            );
          }
        }
      }
    }),
  );
};

export const updateActiveTabState = (updates: Partial<TabState>) => {
  Object.entries(updates).forEach(([key, value]) => {
    setOrderStore('activeTab', key as keyof TabState, value);
  });
};

export const updateOrderFormErrorState = (
  errors: Record<string, string[] | undefined> | null,
) => {
  setOrderStore(() => ({
    orderFormError: errors,
  }));
};

export const updateOrderFormMappedErrorSection = (
  errors: Record<string, string> | null,
) => {
  setOrderStore(() => ({
    mappedErrorSection: errors,
  }));
};

export const updateOrderFormSoftErrors = (field: string, value: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- orderFormSoftErrors can be unset at runtime despite its store type
  if (!orderStore.orderFormSoftErrors) {
    setOrderStore('orderFormSoftErrors', {
      [field]: value,
    });
    return;
  }

  setOrderStore('orderFormSoftErrors', field, value);
};

export const removeOrderFormSoftErrors = (field: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- orderFormSoftErrors can be unset at runtime despite its store type
  if (!orderStore.orderFormSoftErrors) {
    return;
  }

  setOrderStore(
    'orderFormSoftErrors',
    produce((prev) => {
      delete prev[field];
    }),
  );
};

export const resetOrderFormSoftErrors = () => {
  // @ts-expect-error We don't care about this error.
  setOrderStore('orderFormSoftErrors', undefined);
  setOrderStore('orderFormSoftErrors', {});
};

export const updateOrderFormErrorPerSectionState = (
  errors: OrderState['orderFormErrorPerSection'],
) => {
  setOrderStore(
    produce((draft) => {
      draft.orderFormErrorPerSection = errors;
    }),
  );
};

export const validateFieldForOrder = async (
  validatePath: string,
  errorPath: string,
  validationContext?: Record<string, unknown>,
) => {
  const updateErrors = (error: unknown) => {
    const updatedErrors: Record<string, string[] | undefined> = {
      ...(unwrap(orderStore.orderFormError) || {}),
      [errorPath]: error as string[] | undefined,
    };

    if (error === undefined || error === null) delete updatedErrors[errorPath];
    updateOrderFormErrorState(updatedErrors);
  };

  try {
    await orderFormSchema.validateAt(validatePath, orderStore.order, {
      context: validationContext,
    });
    const mappedErrorSection = unwrap(orderStore.mappedErrorSection);
    if (mappedErrorSection && mappedErrorSection[errorPath]) {
      const errorsPerSection = { ...orderStore.orderFormErrorPerSection };
      errorsPerSection[
        mappedErrorSection[errorPath] as keyof typeof errorsPerSection
      ] -= 1;
      updateOrderFormErrorPerSectionState(errorsPerSection);
      delete mappedErrorSection[errorPath];
      updateOrderFormMappedErrorSection(mappedErrorSection);
    }
    updateErrors(undefined);
  } catch (validationError: unknown) {
    if (validationError instanceof ValidationError) {
      updateErrors(validationError.errors);
      await validateOrder();
    }

    logError('validateFieldForOrder', validationError as Error);
  }
};
